import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { useRouter } from '@/hooks';
import * as kpiState from '../state';
import * as globalState from '@/components/Analysis/state';
import { platformName } from '@/state/atoms';
import {
  useGetAggregatedKpiLogEntriesLazyQuery,
  usePlatformQuery,
  GetAggregatedKpiLogEntriesQuery,
  LogCategory,
} from '@/__generated__/graphql';
import { XlsxProcessor } from '@/utils/xlsx';
import { convertValueByUnit, formatLogDate, getSectionUnitSymbol } from '../utils';
import { WorklogsField } from '../types';
import { COLLECTION_NAMES } from '@/constants';

export const useKpiXlsxExport = () => {
  const router = useRouter();
  const { platformId } = router.query;

  const selectedDeploymentIds = useAtomValue(kpiState.selectedDeploymentIds);
  const selectedWorklogsTemplate = useAtomValue(kpiState.selectedWorklogsTemplate);
  const selectedDateAggregation = useAtomValue(kpiState.selectedDateAggregation);
  const selectedStartDate = useAtomValue(kpiState.selectedStartDate);
  const selectedEndDate = useAtomValue(kpiState.selectedEndDate);
  const unitSystem = useAtomValue(globalState.unitSystem);
  const currentPlatformName = useAtomValue(platformName);

  const selectedWorkpackTypes = useAtomValue(kpiState.selectedWorkpackTypes);
  const selectedWbsFilter = useAtomValue(kpiState.selectedWbsFilter);

  const selectedWorkpackTypesFilter = useMemo(() => {
    if (selectedWorkpackTypes.length === 0) return undefined;
    return selectedWorkpackTypes;
  }, [selectedWorkpackTypes]);

  // Fetch platform name from backend only if not available in state
  const { data: platformData } = usePlatformQuery({
    variables: { platformId: platformId as string },
    skip: !!currentPlatformName || !platformId || typeof platformId !== 'string',
  });

  const platFormName = useMemo(() => {
    return currentPlatformName || platformData?.platform?.name || 'Platform';
  }, [currentPlatformName, platformData?.platform?.name]);
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const startDate = selectedStartDate
    ? new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(selectedStartDate)
    : undefined;

  const endDate = selectedEndDate
    ? new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(selectedEndDate)
    : undefined;

  // Lazy query for fetching data on demand
  const [fetchAggregatedKpiLogEntries, { loading: logsLoading }] =
    useGetAggregatedKpiLogEntriesLazyQuery({
      fetchPolicy: 'network-only',
    });

  const templateFields: WorklogsField[] = useMemo(() => {
    return selectedWorklogsTemplate?.fields.filter((field) => field.displayInLogAggregation) ?? [];
  }, [selectedWorklogsTemplate]);

  // Helper to convert value based on field's unit
  const getDisplayValue = useCallback(
    (fieldKey: string, value: number | undefined): number | string => {
      if (value === undefined) return '-';

      // Find the template field to get both unit and decimals
      const templateField = templateFields.find((field) => field.key === fieldKey);
      const fieldUnit = templateField?.fieldUnit;
      const convertedValue = convertValueByUnit(value, fieldUnit, unitSystem, 'view');

      // Apply decimal rounding after unit conversion
      const decimals = templateField?.roundToDecimal ?? 2;

      if (typeof convertedValue === 'number') {
        return Number(convertedValue.toFixed(decimals));
      }

      return convertedValue;
    },
    [unitSystem, templateFields]
  );

  const buildXlsxData = useCallback(
    (
      logs: NonNullable<GetAggregatedKpiLogEntriesQuery['getAggregatedKpiLogEntries']>,
      columnKeys: string[]
    ) => {
      const selectedFields = templateFields.filter((field) => columnKeys.includes(field.key ?? ''));

      // Build columns array
      const columns: Array<{ label: string; data: (string | number)[] }> = [];

      // For weekly/monthly/quarterly: Add separate columns for label and date range
      // For daily: Single date column
      if (
        selectedDateAggregation === 'WEEKLY' ||
        selectedDateAggregation === 'MONTHLY' ||
        selectedDateAggregation === 'QUARTERLY'
      ) {
        // Column 1: Week/Month/Quarter label
        const labelColumn = {
          label: 'Period',
          data: logs.map((log) => {
            const { dayOfWeek } = formatLogDate(
              log.logDate,
              selectedDateAggregation,
              log.startDate || undefined,
              log.endDate || undefined,
              userTimezone
            );
            return dayOfWeek;
          }),
        };
        columns.push(labelColumn);

        // Column 2: Date range
        const dateRangeColumn = {
          label: 'Date Range',
          data: logs.map((log) => {
            const { formattedDate } = formatLogDate(
              log.logDate,
              selectedDateAggregation,
              log.startDate || undefined,
              log.endDate || undefined,
              userTimezone
            );
            return formattedDate;
          }),
        };
        columns.push(dateRangeColumn);
      } else {
        // Daily or other: Single date column
        const dateColumn = {
          label: 'Date',
          data: logs.map((log) => {
            const { formattedDate } = formatLogDate(
              log.logDate,
              selectedDateAggregation,
              log.startDate || undefined,
              log.endDate || undefined,
              userTimezone
            );
            return formattedDate;
          }),
        };
        columns.push(dateColumn);
      }

      // Add data columns for selected fields
      selectedFields.forEach((field) => {
        const key = field.key;
        if (!key) return;

        const unitSymbol = getSectionUnitSymbol(field.fieldUnit, unitSystem);
        const columnLabel = unitSymbol ? `${field.name} (${unitSymbol})` : field.name;

        const columnData = logs.map((log) => {
          const fieldValue = log.fields?.find((f) => f?.fieldKey === key);
          return getDisplayValue(key, fieldValue?.value);
        });

        columns.push({
          label: columnLabel,
          data: columnData,
        });
      });

      return new XlsxProcessor({
        columns,
        sheetName: 'Detailed Worklog Report',
      });
    },
    [templateFields, selectedDateAggregation, userTimezone, unitSystem, getDisplayValue]
  );

  const downloadXLSX = useCallback(
    async (columnKeys: string[]) => {
      try {
        const result = await fetchAggregatedKpiLogEntries({
          variables: {
            input: {
              timezone: userTimezone,
              structureIds: selectedDeploymentIds,
              groupBy: selectedDateAggregation,
              startDate: startDate,
              endDate: endDate,
              workpackTypes: selectedWorkpackTypesFilter,
              wbs: selectedWbsFilter,
              category: [LogCategory.Worklog],
              collectionName: COLLECTION_NAMES.WORKPACK,
            },
          },
        });

        if (result.data?.getAggregatedKpiLogEntries) {
          const xlsxData = buildXlsxData(result.data.getAggregatedKpiLogEntries, columnKeys);
          const timestamp = format(new Date(), 'dd-MMM-yyyy HH-mm-ss');
          const fileName = `${platFormName} - Detailed Worklog Report - ${timestamp}.xlsx`;
          xlsxData.download(fileName);
        } else {
          throw new Error('No data returned from query');
        }
      } catch (error) {
        console.error('Error exporting XLSX:', error);
        throw error;
      }
    },
    [
      fetchAggregatedKpiLogEntries,
      userTimezone,
      selectedDeploymentIds,
      selectedDateAggregation,
      startDate,
      endDate,
      selectedWorkpackTypesFilter,
      selectedWbsFilter,
      buildXlsxData,
      platFormName,
    ]
  );

  return { downloadXLSX, loading: logsLoading };
};
